import { SubmitChargeCardApplicationSuccess } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import {
  cardApplicationAllCheckedSchema,
  cardApplicationSchema,
} from 'features/believe/application/CardApplicationScreen/cardApplication.schema';
import {
  formatFullAddress,
  getBelieveAgreement,
} from 'features/believe/application/CardApplicationScreen/helpers/cardApplication.helpers';
import useBelieveApplication from 'features/believe/application/hooks/useBelieveApplication';
import usePolling from 'features/believe/application/hooks/useBelieveNextAction';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { FeatureFlagKey } from 'services/statsig/types';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import SectionDivider from 'shared/components/vxl/divider/SectionDivider/SectionDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Checkbox from 'shared/components/vxl/selection/Checkbox/Checkbox';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import HyperlinkBaseText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { CUSTOMER_SERVICE_NUMBER } from 'shared/constants/appStore';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { getUuid } from 'shared/utils/getUuid/getUuid';
import { toTitleCase } from 'shared/utils/string/string.helpers';

const CardApplicationScreen = () => {
  const router = useRouter();
  const {
    fetching,
    agreementIdList,
    submitChargeCardApplication,
    userFullName,
    userEmail,
    userPhone,
    userAddressLine1,
    userAddressLine2,
    userCityStateZip,
    mutationFetching,
    mutationData,
    mutationError,
    agreements,
  } = useBelieveApplication();
  const { bottom } = useSafeAreaInsets();
  const { isFeatureEnabled } = useStatsig();
  const isBelieveDay0M2 = isFeatureEnabled(FeatureFlagKey.BELIEVE_DAY_0_M2);
  const subcategory = isBelieveDay0M2
    ? 'believe_day_0_application'
    : 'believe_application';
  const believeAgreementObj = getBelieveAgreement(agreements);
  const { t: translatedText } = useTranslation('believe');

  const initialAgreementOptions: {
    labelText: string;
    checked: boolean;
    name: string;
    analyticsId: string;
  }[] = [
    {
      labelText: '',
      checked: false,
      name: 'believeProgramAgreements',
      analyticsId: 'believeProgramAgreements',
    },
    {
      labelText: translatedText('application.agreements.obtainInformation'),
      checked: false,
      name: 'rightsToObtainInformation',
      analyticsId: 'rightsToObtainInformation',
    },
    {
      labelText: translatedText('application.agreements.grantInterest'),
      checked: false,
      name: 'grantInterest',
      analyticsId: 'grantInterest',
    },
    {
      labelText: translatedText('application.agreements.safePay'),
      checked: false,
      name: 'safePay',
      analyticsId: 'safePay',
    },
  ];

  const [isValid, setIsValid] = useState(false);
  const [isSafePayOn, setIsSafePayOn] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [agreeToAll, setAgreeToAll] = useState(false);
  const [applicationId, setApplicationId] = useState<string>('');
  const [checkBoxOption, setCheckBoxOption] = useState<string>('');
  const [checkboxStatusObject, setCheckBoxStatusObject] = useState(
    initialAgreementOptions,
  );

  useViewDidFocusScreenAnalyticsEvent({
    key: 'cardApplication',
    customAttributes: { subcategory },
  });

  const handleCheckBoxAnalytics = usePressableAnalytics({
    analyticsId: checkBoxOption,
    customAttributes: { subcategory },
  });

  const handleAllCheckAnalytics = usePressableAnalytics({
    analyticsId: 'allCheckBox',
    customAttributes: { subcategory },
  });

  const handleSubmitAnalytics = usePressableAnalytics({
    analyticsId: 'submitApplication',
    customAttributes: { subcategory },
  });

  const handleSubmit = () => {
    setIsSubmitting(true);
    handleSubmitAnalytics();
    void submitChargeCardApplication({
      idempotencyId: getUuid(),
      input: {
        agreements: agreementIdList,
        safePayOptIn: isSafePayOn,
      },
    });
  };
  const { isChecking, setIsChecking } = usePolling(
    applicationId,
    isBelieveDay0M2,
  );
  useEffect(() => {
    if (mutationData && !mutationFetching && !mutationError) {
      setApplicationId(
        (
          mutationData.submitChargeCardApplication as SubmitChargeCardApplicationSuccess
        )?.applicationId,
      );
      setIsChecking(true);
    } else if (mutationError) {
      setIsSubmitting(false);
      router.replace('/believe/apply/cardApplicationError');
    }
  }, [mutationData, mutationError, mutationFetching, setIsChecking, router]);

  const handleAgreeToAll = (currentAgreeToAll: boolean) => {
    setAgreeToAll(!agreeToAll);
    setIsSafePayOn(!agreeToAll);
    handleAllCheckAnalytics();
    const updatedStatusObject = checkboxStatusObject.map((object) => {
      return currentAgreeToAll
        ? { ...object, checked: true }
        : { ...object, checked: false };
    });
    setCheckBoxStatusObject(updatedStatusObject);
  };

  const handleSelection = (name: string) => {
    switch (name) {
      case 'safePay':
        setIsSafePayOn(!isSafePayOn);
        setCheckBoxOption(name);
        handleCheckBoxAnalytics();
        setCheckBoxOption('');
        break;

      case 'believeProgramAgreements':
      case 'rightsToObtainInformation':
      case 'grantInterest':
        setCheckBoxOption(name);
        handleCheckBoxAnalytics();
        setCheckBoxOption('');
        break;
    }

    const updatedStatusObject = checkboxStatusObject.map((object) => {
      return object.name === name
        ? { ...object, checked: !object.checked }
        : object;
    });
    const allChecked =
      cardApplicationAllCheckedSchema.safeParse(updatedStatusObject);

    setAgreeToAll(allChecked.success);
    setCheckBoxStatusObject(updatedStatusObject);
  };

  useEffect(() => {
    const valid = cardApplicationSchema.safeParse(checkboxStatusObject);

    setIsValid(valid.success);
  }, [checkboxStatusObject]);

  const varoBelievePressAgreementAnalytics = usePressableAnalytics({
    analyticsId: 'varoBelieveProgramAgreement',
    customAttributes: { subcategory },
  });

  const handleAgreementLinkPress = () => {
    varoBelievePressAgreementAnalytics();
    const url = believeAgreementObj?.resource?.pdfDocument?.url;
    const params = {
      agreementLink: encodeURIComponent(url!),
    };
    router.push({
      pathname: '/believe/apply/believe-agreements',
      params,
    });
  };

  return (
    <>
      <Stack.Screen
        options={{
          title: isBelieveDay0M2
            ? translatedText(
                'application.applicationScreenTitle.believeApplication',
              )
            : translatedText(
                'application.applicationScreenTitle.cardApplication',
              ),
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScrollView
        style={styles.container}
        contentContainerStyle={{ paddingBottom: bottom }}
      >
        <View>
          {isBelieveDay0M2 ? (
            <View style={styles.reviewInfoSection}>
              <TitleLgText numberOfLines={0} style={styles.reviewInfoContainer}>
                {translatedText(
                  'application.applicationScreenTitle.reviewInfoHeader',
                )}
              </TitleLgText>
              <BodyBaseText numberOfLines={0}>
                {translatedText(
                  'application.applicationScreenTitle.reviewInfoDetails',
                )}
              </BodyBaseText>
            </View>
          ) : (
            <View style={styles.reviewItemContainer}>
              <BodyBaseText>
                {translatedText(
                  'application.applicationScreenTitle.reviewString',
                )}
              </BodyBaseText>
              <BodyXSText>
                {`${translatedText('application.applicationScreenTitle.customerServiceString')} ${CUSTOMER_SERVICE_NUMBER}`}
              </BodyXSText>
            </View>
          )}
          <ListItem
            primaryCopy={toTitleCase(userFullName)}
            secondaryCopy={translatedText('application.addressReview.name')}
            lineFormat={ListItemLineFormat.LABEL}
            showsChevron={false}
          />
          <View style={styles.hairLineContainer}>
            <HairLineDivider />
          </View>
          <ListItem
            primaryCopy={userEmail}
            secondaryCopy={translatedText('application.addressReview.email')}
            lineFormat={ListItemLineFormat.LABEL}
            showsChevron={false}
          />
          <View style={styles.hairLineContainer}>
            <HairLineDivider />
          </View>
          {!isBelieveDay0M2 ? (
            <>
              <ListItem
                primaryCopy={userPhone}
                secondaryCopy={translatedText(
                  'application.addressReview.phone',
                )}
                lineFormat={ListItemLineFormat.LABEL}
                showsChevron={false}
              />
              <View style={styles.hairLineContainer}>
                <HairLineDivider />
              </View>
            </>
          ) : null}
          <ListItem
            primaryCopy={formatFullAddress(
              userAddressLine1,
              userAddressLine2,
              userCityStateZip,
            )}
            secondaryCopy={translatedText('application.addressReview.address')}
            lineFormat={ListItemLineFormat.LABEL}
            showsChevron={false}
            primaryNumberOfLines={3}
          />
          {isBelieveDay0M2 && (
            <>
              <View style={styles.separator} testID="separator" />
              <SectionDivider
                labelText={translatedText(
                  'application.applicationScreenTitle.termsSectionDivider',
                )}
              />
            </>
          )}
          <View style={styles.checkBoxContainer}>
            {initialAgreementOptions.map((option, index) => {
              return (
                <Checkbox
                  testId="checkbox"
                  key={option.labelText as string}
                  checked={checkboxStatusObject[index].checked}
                  onSelectedStateChange={() => handleSelection(option.name)}
                  analyticsId={option.analyticsId}
                  customAttributes={{ subcategory }}
                >
                  {option.name === 'believeProgramAgreements' ? (
                    <View style={styles.checkboxText}>
                      <View>
                        <BodyBaseText style={styles.checkboxText}>
                          {translatedText(
                            'application.agreements.believeProgramAgreements.firstPart',
                          )}{' '}
                          <HyperlinkBaseText onPress={handleAgreementLinkPress}>
                            {translatedText(
                              'application.agreements.believeProgramAgreements.linkText',
                            )}
                          </HyperlinkBaseText>{' '}
                          {translatedText(
                            'application.agreements.believeProgramAgreements.secondPart',
                          )}
                        </BodyBaseText>
                      </View>
                    </View>
                  ) : (
                    <View style={styles.checkboxText}>
                      <BodyBaseText style={styles.checkboxText}>
                        {option.labelText}
                      </BodyBaseText>
                    </View>
                  )}
                </Checkbox>
              );
            })}
          </View>
        </View>
      </ScrollView>
      <View style={[styles.parentContainer, { paddingBottom: bottom }]}>
        <HairLineDivider />
        <View style={styles.checkButtonContainer}>
          <Checkbox
            testId="agree-to-all"
            labelText={translatedText('application.agreements.agreeToAll')}
            checked={agreeToAll}
            onSelectedStateChange={() => handleAgreeToAll(!agreeToAll)}
            analyticsId="agreeToAll"
          />
        </View>
        <View style={styles.buttonContainer}>
          <Button
            label={translatedText('application.submitApplication')}
            isDisabled={!isValid}
            isLoading={isSubmitting || isChecking}
            type="PRIMARY"
            onPress={() => {
              handleSubmit();
            }}
          />
        </View>
      </View>
      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={null}
      />
    </>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.s,
  },
  checkBoxContainer: {
    flexGrow: 1,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.s,
    width: '100%',
  },
  checkButtonContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    paddingTop: theme.spacing.m,
  },
  checkboxText: {
    flex: 1,
    marginHorizontal: 0,
    paddingHorizontal: 0,
    width: '100%',
  },
  container: {
    backgroundColor: theme.color.background.primary,
    gap: theme.spacing.m,
  },
  hairLineContainer: {
    paddingHorizontal: theme.spacing.m,
  },
  parentContainer: {
    backgroundColor: theme.color.background.primary,
  },
  reviewItemContainer: {
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  reviewInfoContainer: {
    paddingVertical: theme.spacing.m,
  },
  reviewInfoSection: {
    paddingHorizontal: theme.spacing.m,
  },
  separator: {
    height: 8,
    backgroundColor: theme.color.background.tertiary,
  },
});

export default CardApplicationScreen;
